import React from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Circle,
  InfoWindow,
} from "@react-google-maps/api";
import { KEY_GOOGLE_MAP, LIBRARIES } from "../../Constants";
import pinMarkerBlue from "../../images/GoogleMaps/pin.marker.blue.png";
import pinMarkerGray from "../../images/GoogleMaps/pin.marker.gray.png";
import { useTranslation } from "react-i18next";

export default function GoogleMapMultiple({
  maxWidth = 0,
  maxHeight = 0,
  // setLat = null,
  // setLng = null,
  // setRadius = null,
  markerArray = [],
  selectedPosition = null,
  markPin = null,
  // setSelectedPosition = () => {},
  resetMap,
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: KEY_GOOGLE_MAP,
    libraries: LIBRARIES,
  });

  const mapRef = React.useRef(null);
  // const debounceRef = React.useRef(null);
  const [hoveredMarker, setHoveredMarker] = React.useState(null);
  const [selectedMarker, setSelectedMarker] = React.useState(null);

  const activeMarker = selectedMarker ?? hoveredMarker;

  const mapWrapperStyle = React.useMemo(
    () => ({
      width: maxWidth > 0 ? `${maxWidth}px` : "100%",
      height: maxHeight > 0 ? `${maxHeight}px` : "100%",
      borderRadius: "8px",
      overflow: "hidden",
    }),
    [maxWidth, maxHeight],
  );

  const center = React.useMemo(() => ({ lat: 13.7563, lng: 100.5018 }), []);

  const handleOnLoad = React.useCallback((map) => {
    mapRef.current = map;
  }, []);

  // const handleOnIdle = React.useCallback(() => {
  //   if (!mapRef.current) return;
  //   if (!window.google?.maps?.geometry?.spherical) return;

  //   const map = mapRef.current;
  //   const center = map.getCenter();
  //   const bounds = map.getBounds();
  //   if (!center || !bounds) return;

  //   clearTimeout(debounceRef.current);
  //   debounceRef.current = setTimeout(() => {
  //     const northEast = bounds.getNorthEast();
  //     const radius =
  //       window.google.maps.geometry.spherical.computeDistanceBetween(
  //         center,
  //         northEast,
  //       );
  //     if (setLat) setLat(center.lat());
  //     if (setLng) setLng(center.lng());
  //     if (setRadius) setRadius(Math.round(radius));
  //   }, 200);
  // }, [setLat, setLng, setRadius]);

  React.useEffect(() => {
    if (!selectedPosition || !mapRef.current) return;
    mapRef.current.panTo(selectedPosition);
    mapRef.current.setZoom(18);
    // setSelectedPosition(null);
  }, [selectedPosition]);

  React.useEffect(() => {
    if (!markPin || !mapRef.current) return;

    const lat = Number(markPin.lat);
    const lng = Number(markPin.long);

    if (isNaN(lat) || isNaN(lng)) return;

    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(18);
  }, [markPin]);

  const markPinPosition = React.useMemo(() => {
    if (!markPin) return null;
    const lat = Number(markPin.lat);
    const lng = Number(markPin.long);
    if (isNaN(lat) || isNaN(lng)) return null;
    const radius = Number(markPin.radius);

    return { lat, lng, radius };
  }, [markPin]);

  React.useEffect(() => {
    if (!mapRef.current) return;
    if (!window.google) return;

    const map = mapRef.current;
    const bounds = new window.google.maps.LatLngBounds();
    let markerCount = 0;
    markerArray.forEach((item) => {
      if (!isNaN(item.lat) && !isNaN(item.lng)) {
        bounds.extend({ lat: item.lat, lng: item.lng });
        markerCount += 1;
      }
    });

    if (markPinPosition) {
      bounds.extend({
        lat: markPinPosition.lat,
        lng: markPinPosition.lng,
      });
      markerCount += 1;
    }

    if (markerCount === 1) {
      const pos = bounds.getCenter();
      map.panTo(pos);
      map.setZoom(18);
      return;
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 80);
    }
  }, [markerArray, markPinPosition, resetMap]);

  if (loadError) {
    return (
      <div style={mapWrapperStyle}>
        <p style={{ padding: 16 }}>เกิดข้อผิดพลาดในการโหลด Google Maps</p>
      </div>
    );
  }

  if (!isLoaded) {
    return <div style={{ ...mapWrapperStyle, background: "#eee" }} />;
  }

  return (
    <div style={mapWrapperStyle}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={12}
        onLoad={handleOnLoad}
        // onIdle={handleOnIdle}
        onClick={() => setSelectedMarker(null)}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
        }}
      >
        {markerArray.map((item, index) => (
          <React.Fragment key={index}>
            <Marker
              position={{ lat: item.lat, lng: item.lng }}
              icon={{
                url: !markPinPosition ? pinMarkerBlue : pinMarkerGray,
                scaledSize: new window.google.maps.Size(27, 43),
                anchor: new window.google.maps.Point(13.5, 21.5),
              }}
              onMouseOver={() => setHoveredMarker(item)}
              onMouseOut={() => setHoveredMarker(null)}
              onClick={() =>
                setSelectedMarker((prev) =>
                  prev?.lat === item.lat && prev?.lng === item.lng
                    ? null
                    : item,
                )
              }
            />
            {item.radius > 0 && (
              <Circle
                center={{ lat: item.lat, lng: item.lng }}
                radius={item.radius}
                options={{
                  strokeColor: !markPinPosition ? "#6393F2" : "#C0C7D6",
                  strokeOpacity: 0.8,
                  strokeWeight: 1,
                  fillColor: !markPinPosition ? "#6393F2" : "#C0C7D6",
                  fillOpacity: 0.2,
                }}
              />
            )}
          </React.Fragment>
        ))}

        {markPinPosition && (
          <React.Fragment>
            <Marker
              zIndex={999}
              position={markPinPosition}
              icon={{
                url: pinMarkerBlue,
                scaledSize: new window.google.maps.Size(27, 43),
                anchor: new window.google.maps.Point(13.5, 21.5),
              }}
            />
            {markPinPosition.radius > 0 && (
              <Circle
                center={{ lat: markPinPosition.lat, lng: markPinPosition.lng }}
                radius={markPinPosition.radius}
                options={{
                  strokeColor: "#6393F2",
                  strokeOpacity: 0.8,
                  strokeWeight: 1,
                  fillColor: "#6393F2",
                  fillOpacity: 0.4,
                }}
              />
            )}
          </React.Fragment>
        )}

        {activeMarker && (
          <InfoWindow
            position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
            options={{
              pixelOffset: new window.google.maps.Size(0, -45),
              disableAutoPan: true,
              headerDisabled: true,
            }}
          >
            <TooltipContent
              item={activeMarker}
              onClose={() => {
                setSelectedMarker(null);
                setHoveredMarker(null);
              }}
            />
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

function TooltipContent({ item, onClose }) {
  const { t } = useTranslation();

  const Row = ({ label, children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <span style={{ fontSize: 11, color: "#bbb", minWidth: 60 }}>{label}</span>
      <span style={{ fontSize: 11, color: "#888", flex: 1 }}>{children}</span>
    </div>
  );

  return (
    <div
      style={{
        width: 220,
        padding: "12px 14px",
        background: "#fff",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: "#111" }}>
            {item?.name ?? "Unknown"}
          </span>
          {item.remark && (
            <span style={{ fontSize: 11, color: "#aaa" }}>{item.remark}</span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0 0 0 8px",
            color: "#bbb",
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      <div style={{ height: 1, background: "#f0f0f0" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Row label={t("hrms_setting_location_form_title_en")}>
          {item.nameEn || "-"}
        </Row>
        <Row label={t("hrms_setting_location_form_title_th")}>
          {item.nameTh || "-"}
        </Row>
        <Row label={t("hrms_setting_location_form_lat")}>{item.lat}</Row>
        <Row label={t("hrms_setting_location_form_long")}>{item.lng}</Row>
        <Row label={t("hrms_setting_location_radius_header")}>
          {Number(item.radius).toLocaleString() || "-"}
        </Row>

        <Row label={t("hrms_setting_location_assign_to")}>
          {item.assignTo?.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {item.assignTo.map((a, i) => (
                <AssignTag key={i} orgInfo={a.orgInfo} />
              ))}
            </div>
          ) : (
            "-"
          )}
        </Row>
        <Row label={t("hrms_setting_location_form_status")}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 20,
              padding: "0 8px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500,
              backgroundColor: item.status
                ? "var(--color-functional-foreground-success-subtle)"
                : "var(--color-functional-foreground-error-subtle)",
              color: item.status
                ? "var(--color-functional-content-success)"
                : "var(--color-functional-content-error)",
            }}
          >
            {item.status
              ? t("hrms_setting_location_active_status")
              : t("hrms_setting_location_inactive_status")}
          </span>
        </Row>
      </div>
    </div>
  );
}

const AssignTag = ({ orgInfo }) => (
  <span
    style={{
      backgroundColor: orgInfo.colorBgPrimary,
      color: orgInfo.colorTextPrimary,
      fontSize: 11,
      fontWeight: 600,
      height: 22,
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 20,
      padding: "0 10px",
    }}
  >
    {orgInfo.orgShortCode}
  </span>
);
